import { useEffect, useRef, useState } from "react";

import type { FormField, FormSchema } from "@/lib/database";
import {
	isRangeSliderMode,
	normalizeRangeSliderValue,
	normalizeSingleSliderValue,
} from "@/lib/fields/slider-utils";

const getDefaultValueForField = (field: FormField): any => {
	switch (field.type) {
		case "tags":
			return [];
		case "checkbox":
			return [];
		case "radio":
			return "";
		case "select":
			return "";
		case "slider":
			if (isRangeSliderMode(field.settings)) {
				return normalizeRangeSliderValue(field.settings);
			}
			return normalizeSingleSliderValue(field.settings);
		case "number":
			return "";
		case "text":
		case "email":
		case "textarea":
		default:
			return "";
	}
};

const initializeFormData = (fields: FormField[]): Record<string, any> => {
	const formData: Record<string, any> = {};

	fields.forEach((field) => {
		formData[field.id] = getDefaultValueForField(field);
	});

	return formData;
};

export function useFormPreviewState(
	schema: FormSchema,
	selectedBlockId?: string | null
) {
	const [formData, setFormData] = useState<Record<string, any>>({});
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const initializedFieldsRef = useRef<Set<string>>(new Set());

	const isMultiStep = schema.settings?.multiStep || schema.blocks?.length > 1;
	// blocks=[] (пустой массив — truthy) ломает `blocks?.flatMap(...) || schema.fields`:
	// [].flatMap() = [] → поля из schema.fields терялись. Проверяем через length.
	const allFields =
		schema.blocks?.length
			? schema.blocks.flatMap((block) => block.fields)
			: schema.fields || [];
	const currentStep = isMultiStep ? schema.blocks?.[currentStepIndex] : null;
	const currentStepFields = isMultiStep ? currentStep?.fields || [] : allFields;

	useEffect(() => {
		const currentFieldIds = new Set(allFields.map((field) => field.id));
		const newFieldIds = [...currentFieldIds].filter(
			(id) => !initializedFieldsRef.current.has(id)
		);

		if (newFieldIds.length > 0) {
			const newFormData = { ...formData };
			allFields.forEach((field) => {
				if (newFieldIds.includes(field.id)) {
					newFormData[field.id] = getDefaultValueForField(field);
				}
			});
			setFormData(newFormData);
			newFieldIds.forEach((id) => initializedFieldsRef.current.add(id));
		}
	}, [allFields.length]);

	useEffect(() => {
		if (selectedBlockId && schema.blocks) {
			const blockIndex = schema.blocks.findIndex(
				(block) => block.id === selectedBlockId
			);
			if (blockIndex !== -1) {
				setCurrentStepIndex(blockIndex);
			}
		}
	}, [selectedBlockId, schema.blocks]);

	const handleFieldValueChange = (fieldId: string, value: any) => {
		setFormData((prev) => ({ ...prev, [fieldId]: value }));
	};

	const nextStep = () => {
		const nextIndex = currentStepIndex + 1;
		if (nextIndex < (schema.blocks?.length || 1)) {
			setCurrentStepIndex(nextIndex);
			return nextIndex;
		}
		return currentStepIndex;
	};

	const prevStep = () => {
		const prevIndex = currentStepIndex - 1;
		if (prevIndex >= 0) {
			setCurrentStepIndex(prevIndex);
			return prevIndex;
		}
		return currentStepIndex;
	};

	const goToStep = (stepIndex: number) => {
		if (stepIndex >= 0 && stepIndex < (schema.blocks?.length || 1)) {
			setCurrentStepIndex(stepIndex);
		}
	};

	const fieldVisibility: Record<
		string,
		{ visible: boolean; disabled: boolean }
	> = {};
	allFields.forEach((f) => {
		fieldVisibility[f.id] = { visible: true, disabled: false };
	});

	return {
		formData,
		currentStepIndex,
		isMultiStep,
		allFields,
		currentStep,
		currentStepFields,
		handleFieldValueChange,
		nextStep,
		prevStep,
		goToStep,
		fieldVisibility,
	};
}
